import {
  GovernmentFundedDetailValue,
  governmentFundedProgramNames,
  RDGovernmentFunded,
} from '@cibo/core'
import { Markdown, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Checkbox, FormControlLabel, FormGroup, Stack, TextField, Typography } from '@mui/material'
import { useDebounceCallback } from '@react-hook/debounce'
import { equals } from 'ramda'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../context'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../queries'

type GovernmentFundedDetail = {
  traitId: 'governmentFunded'
  year: number
  value: GovernmentFundedDetailValue
  input: never
  result: never
}

const detailIsValid = (value: Partial<GovernmentFundedDetailValue>) => {
  return (
    value &&
    (value.isFunded === false ||
      (value.isFunded === true &&
        !!value.data &&
        (!!value.data.programs?.length || value.data.otherProgram !== '')))
  )
}

const initialValue = {
  isFunded: undefined,
  data: {
    programs: [],
    otherProgram: undefined,
  },
}

export const RDGovernmentFundedEditor = ({
  detailRequirements,
}: ResourceDetailFeatureTaskEditorProps<RDGovernmentFunded>) => {
  const { t } = useTranslation('@cibo/programs/GovernmentFundedEditor')
  const textInput = useRef<HTMLInputElement>(null)

  const { engagementId } = useProgramEngagementContext()
  const details = useProgramEngagementDetails<any>({
    resourceId: engagementId,
    detailRequirements: [
      {
        traitId: 'governmentFunded',
        year: detailRequirements[0]?.year,
        dataType: 'workflow',
      },
    ],
  })
  const updateEngagementDetails = useUpdateProgramEngagementDetails<GovernmentFundedDetail>({
    engagementId: engagementId as string,
  })

  const isImmutable = details.data?.[0]?.immutable

  const [localValue, setLocalValue] = useState<Partial<GovernmentFundedDetailValue>>(initialValue)

  // This effect sets the initial value from persisted value
  useEffect(() => {
    if (details && !!details.data?.length) {
      const detail = details.data[0]
      setLocalValue(detail.value)
    }
  }, [details.isFetched])

  // this effect synchronizes the local and persisted values
  useEffect(() => {
    if (equals(localValue, details.data?.[0]?.value)) {
      return
    } else {
      persistDetailValue(localValue)
    }
  }, [localValue, details.data])

  const persistDetailValue = useDebounceCallback((value: Partial<GovernmentFundedDetailValue>) => {
    const shouldSend = value === undefined || (value !== initialValue && detailIsValid(value))
    engagementId &&
      shouldSend &&
      updateEngagementDetails
        .mutateAsync({
          resourceId: engagementId,
          details: [
            {
              traitId: 'governmentFunded',
              year: detailRequirements[0]?.year as number,
              value: value as GovernmentFundedDetailValue,
            } as GovernmentFundedDetail,
          ],
        })
        .catch(() => {
          setLocalValue(details.data?.[0]?.value || initialValue)
        })
  }, 300)

  const clearValue = () =>
    updateEngagementDetails.mutateAsync({
      resourceId: engagementId as string,
      details: [
        {
          traitId: 'governmentFunded',
          year: detailRequirements[0]?.year as number,
          value: undefined as unknown as GovernmentFundedDetailValue,
        } as GovernmentFundedDetail,
      ],
    })

  const onClickNo = (_: any, checked: boolean) => {
    setLocalValue({
      isFunded: !checked,
      data: {
        programs: [],
        otherProgram: undefined,
      },
    })
  }

  const onClickProgram = (event: any, checked: boolean) => {
    const programs = localValue.data?.programs || []
    const newPrograms =
      programs.includes(event.target.value) && !checked
        ? programs.filter(program => program !== event.target.value)
        : [...programs, event.target.value]
    if (!checked && newPrograms.length === 0) {
      // when unselecting AND no other programs are selected, set to empty value
      clearValue()
      setLocalValue(initialValue)
    } else {
      setLocalValue({
        isFunded: true,
        data: {
          programs: newPrograms,
          otherProgram: localValue.data?.otherProgram,
        },
      })
    }
  }

  const onClickOtherCheckbox = (event: any, checked: boolean) => {
    if (checked) {
      ;(
        textInput.current?.getElementsByClassName('MuiInputBase-input')[0] as HTMLInputElement
      )?.focus()
    }
    if (!checked) {
      setLocalValue(prev => {
        if (!prev.data?.programs?.length) {
          clearValue()
          return initialValue
        }
        return {
          isFunded: prev.isFunded,
          data: {
            ...prev.data,
            otherProgram: undefined,
          },
        }
      })
    } else {
      setLocalValue(prev => {
        return {
          isFunded: true,
          data: {
            ...prev.data,
            otherProgram: checked ? prev.data?.otherProgram || '' : undefined,
          },
        }
      })
    }
  }

  return (
    <Stack>
      <Markdown>{t('question')}</Markdown>

      <FormGroup>
        <FormControlLabel
          onChange={onClickNo}
          control={<Checkbox />}
          label={t('no')}
          value="no"
          checked={localValue.isFunded === false}
          disabled={isImmutable}
        />
        {governmentFundedProgramNames.map(program => (
          <FormControlLabel
            key={program}
            onChange={onClickProgram}
            control={<Checkbox />}
            label={t(`governmentProgram_${program}`)}
            value={program}
            checked={localValue.data?.programs?.includes(program) || false}
            disabled={isImmutable}
          />
        ))}
        <FormControlLabel
          control={<Checkbox />}
          checked={localValue.data?.otherProgram !== undefined}
          onChange={onClickOtherCheckbox}
          disabled={isImmutable}
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>{t('other')}</Typography>
              <TextField
                slotProps={{
                  input: {
                    ref: textInput,
                  },
                }}
                value={localValue.data?.otherProgram || ''}
                placeholder={t('enterProgramName')}
                disabled={isImmutable}
                onBlur={() => {
                  if (localValue.data?.otherProgram === '') {
                    setLocalValue(prev => {
                      // when no text AND no other programs are selected, set to empty value
                      if (!prev.data?.programs?.length && !!prev.isFunded) {
                        clearValue()
                        return initialValue
                      } else {
                        return {
                          isFunded: prev.isFunded,
                          data: {
                            ...prev.data,
                            otherProgram: undefined,
                          },
                        }
                      }
                    })
                  }
                }}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setLocalValue(prev => ({
                    isFunded: true,
                    data: {
                      ...prev.data,
                      otherProgram: event.target.value,
                    },
                  }))
                }}
              />
            </Stack>
          }
          value="other"
        />
      </FormGroup>
    </Stack>
  )
}
